import { createContext, useContext, useEffect, useRef, useState, type ReactNode } from "react";
import type { Session, User } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";
import { lovable } from "@/integrations/lovable";
import { sendWelcomeEmail } from "@/lib/email/app-emails.functions";
import { completeSignOut } from "@/lib/sign-out-flow";

async function maybeSendWelcome(user: User) {
  try {
    if (!user.email || !user.email_confirmed_at) return;
    const { data: prof } = await supabase
      .from("profiles")
      .select("welcome_sent_at, display_name")
      .eq("id", user.id)
      .maybeSingle();
    if (!prof || prof.welcome_sent_at) return;
    await sendWelcomeEmail({ data: { name: prof.display_name ?? undefined } });
    await supabase
      .from("profiles")
      .update({ welcome_sent_at: new Date().toISOString() })
      .eq("id", user.id);
  } catch (err) {
    console.warn("welcome email failed", err);
  }
}

export type Profile = {
  id: string;
  display_name: string | null;
  avatar_url: string | null;
  tier: "free" | "standard" | "pro" | "elite";
};

type AuthContextValue = {
  user: User | null;
  session: Session | null;
  profile: Profile | null;
  loading: boolean;
  /** True once Supabase has attempted to restore the session from storage. */
  isHydrated: boolean;
  signingOut: boolean;
  signIn: (email: string, password: string) => Promise<{ error: string | null }>;
  signUp: (
    email: string,
    password: string,
    displayName: string,
  ) => Promise<{ error: string | null }>;
  /** returnTo: same-origin public path to land on after the provider returns ("/dashboard" default, "/app" for the phone app). */
  signInWithGoogle: (returnTo?: string) => Promise<{ error: string | null; dismissed?: boolean }>;
  signInWithApple: (returnTo?: string) => Promise<{ error: string | null; dismissed?: boolean }>;
  signOut: () => Promise<void>;
  refreshProfile: () => Promise<void>;
};

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(true);
  const [isHydrated, setIsHydrated] = useState(false);
  const [signingOut, setSigningOut] = useState(false);
  const signOutTask = useRef<Promise<void> | null>(null);
  const activeUserId = useRef<string | null>(null);

  const loadProfile = async (userId: string) => {
    const { data } = await supabase
      .from("profiles")
      .select("id, display_name, avatar_url, tier")
      .eq("id", userId)
      .maybeSingle();
    if (activeUserId.current !== userId) return;
    setProfile(
      (data as Profile | null) ?? {
        id: userId,
        display_name: null,
        avatar_url: null,
        tier: "free",
      },
    );
  };

  useEffect(() => {
    // Set up listener FIRST
    const { data: sub } = supabase.auth.onAuthStateChange((_event, newSession) => {
      activeUserId.current = newSession?.user.id ?? null;
      setSession(newSession);
      setUser(newSession?.user ?? null);
      if (newSession?.user) {
        // Defer to avoid deadlock
        setTimeout(() => loadProfile(newSession.user.id), 0);
        if (_event === "SIGNED_IN") {
          setTimeout(() => maybeSendWelcome(newSession.user), 0);
        }
      } else {
        setProfile(null);
      }
    });

    // Then check existing session
    supabase.auth.getSession().then(({ data: { session: existing } }) => {
      activeUserId.current = existing?.user.id ?? null;
      setSession(existing);
      setUser(existing?.user ?? null);
      if (existing?.user) loadProfile(existing.user.id);
      setLoading(false);
      setIsHydrated(true);
    });

    return () => sub.subscription.unsubscribe();
  }, []);

  // Realtime: refresh profile when subscriptions or profile row change
  useEffect(() => {
    if (!user) return;
    const channel = supabase
      .channel(`profile-sync-${user.id}`)
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "subscriptions", filter: `user_id=eq.${user.id}` },
        () => loadProfile(user.id),
      )
      .on(
        "postgres_changes",
        { event: "UPDATE", schema: "public", table: "profiles", filter: `id=eq.${user.id}` },
        () => loadProfile(user.id),
      )
      .subscribe();
    return () => {
      supabase.removeChannel(channel);
    };
  }, [user?.id]);

  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    return { error: error?.message ?? null };
  };

  const signUp = async (email: string, password: string, displayName: string) => {
    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: { display_name: displayName },
        emailRedirectTo: `${window.location.origin}/dashboard`,
      },
    });
    return { error: error?.message ?? null };
  };

  const oauth = async (provider: "google" | "apple", returnTo = "/dashboard") => {
    const { nativePlatform } = await import("@/lib/native-push");
    if (provider === "google" && nativePlatform() === "android") {
      const native = await import("@/lib/native-google");
      if (native.nativeGoogleAvailable()) {
        try {
          const err = await native.nativeGoogleSignIn();
          return { error: err };
        } catch (e) {
          const msg = e instanceof Error ? e.message : "";
          // Closing the account sheet is not an error.
          if (/cancel/i.test(msg)) return { error: null, dismissed: true };
          return { error: msg || "Google sign-in failed" };
        }
      }
    }
    // Native shell: OAuth finishes in the system browser, which then bounces
    // the session back into the app through the custom URL scheme.
    const path = nativePlatform() ? "/app/native-return" : returnTo === "/app" ? "/app" : "/dashboard";
    const result = await lovable.auth.signInWithOAuth(provider, {
      redirect_uri: `${window.location.origin}${path}`,
    });
    const label = provider === "apple" ? "Apple" : "Google";
    if (result.error) return { error: result.error.message ?? `${label} sign-in failed` };
    return { error: null };
  };
  const signInWithGoogle = (returnTo?: string) => oauth("google", returnTo);
  const signInWithApple = (returnTo?: string) => oauth("apple", returnTo);

  const signOut = () => {
    if (signOutTask.current) return signOutTask.current;
    setSigningOut(true);
    const task = completeSignOut({
      disconnectPush: async () => {
        const { disconnectNativePush } = await import("@/lib/native-push");
        return disconnectNativePush();
      },
      unlinkDevice: async deviceId => {
        const { error } = await supabase.from("device_push_tokens").delete()
          .eq("subscription_id", deviceId).eq("user_id", user?.id ?? "");
        if (error) throw new Error("Device unlink failed");
      },
      endSession: async () => {
        const { error } = await supabase.auth.signOut({ scope: "local" });
        if (error) {
          const { data } = await supabase.auth.getSession();
          if (data.session) throw new Error("Sign out could not be completed. Please retry.");
        }
      },
      clearAccount: () => {
        activeUserId.current = null;
        setSession(null);
        setUser(null);
        setProfile(null);
      },
      warn: () => console.warn("Optional sign-out cleanup did not complete"),
    }).finally(() => { signOutTask.current = null; setSigningOut(false); });
    signOutTask.current = task;
    return task;
  };

  const refreshProfile = async () => {
    if (user) await loadProfile(user.id);
  };

  return (
    <AuthContext.Provider
      value={{ user, session, profile, loading, isHydrated, signingOut, signIn, signUp, signInWithGoogle, signInWithApple, signOut, refreshProfile }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
